import React, { useEffect, useState } from 'react';
import { Button } from 'react-style-guide';
import { useTranslation } from 'react-utilities';
import AccountReactivationPageLoader from '../../accountReactivation/AccountReactivationPageLoader';
import {
  useNotApprovedPagePunishmentContext,
  NotApprovedPagePunishmentContextStatus
} from '../context/NotApprovedPagePunishmentProvider';
import { EventTypes } from '../services/eventConstants';
import { PUNISHMENT_TYPE_TO_STRING_KEY } from '../utils/constants';
import determineProceedAction from '../utils/determineProceedAction';
import logout from '../utils/logout';
import { ProceedAction } from '../utils/types';
import sendNotApprovedPageEvent from '../services/sendNotApprovedPageEvent';
import PageItemsFromConfigs from '../pageItemConfigs/PageItemsFromConfigs';
import canReactivate from '../utils/canReactivate';
import NotApprovedPageProceedCTAButton from './NotApprovedProceedCTAButton';

const NotApprovedMainPage = (): JSX.Element | null => {
  const punishmentContext = useNotApprovedPagePunishmentContext();
  const { translate } = useTranslation();

  useEffect(
    () => {
      if (
        punishmentContext.status === NotApprovedPagePunishmentContextStatus.SUCCESS &&
        punishmentContext.punishment
      ) {
        const punishmentData = punishmentContext.punishment;
        sendNotApprovedPageEvent(EventTypes.PageRendered, {
          interventionId: punishmentData.interventionId ?? '',
          punishedUserId: punishmentData.punishedUserId,
          isReactivationEligible: canReactivate(
            punishmentData.punishmentTypeDescription,
            punishmentData.endDate
          ),
          verificationCategory: punishmentData.verificationCategory
        });
      }
    },
    // We only want this hook to run when the status changes once the punishment is loaded, so we only include
    // punishmentContext.status as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [punishmentContext.status]
  );

  const [reactivateAckIsChecked, setReactivateAckIsChecked] = useState(false);

  if (punishmentContext.status === NotApprovedPagePunishmentContextStatus.LOADING) {
    // TODO(nngai) A more elegant loading state would be good.
    return null;
  }

  if (punishmentContext.status === NotApprovedPagePunishmentContextStatus.ERROR) {
    // TODO(nngai) A more elegant error state would be good.
    console.error(punishmentContext.error);
    return null;
  }

  const punishmentData = punishmentContext.punishment;

  if (!punishmentData) {
    // Redirect home if a punishment was not found.
    window.location.replace('/home');
    return null;
  }

  if (punishmentData.context.SelfServiceDeactivated) {
    return <AccountReactivationPageLoader />;
  }

  // Determine what the next action the user must do to proceed is.
  const proceedAction = determineProceedAction(punishmentData);

  const { verificationCategory, punishmentTypeDescription } = punishmentData;

  const punishmentTypeLabel = verificationCategory
    ? translate('Heading.AccountBanned')
    : translate(PUNISHMENT_TYPE_TO_STRING_KEY[punishmentTypeDescription] ?? '');

  return (
    <div className='not-approved-content-container' data-testid='main-page-container'>
      <div className='not-approved-title' data-testid='main-page-title'>
        {punishmentTypeLabel}
      </div>
      <div className='not-approved-info-section'>
        <PageItemsFromConfigs punishmentData={punishmentData} />
      </div>
      <div className='not-approved-action-section'>
        {proceedAction === ProceedAction.Reactivate ? (
          <div className='not-approved-action-section-children'>
            <input
              id='acknowledgement'
              className='not-approved-checkbox'
              type='checkbox'
              onClick={() => {
                setReactivateAckIsChecked(!reactivateAckIsChecked);
                sendNotApprovedPageEvent(EventTypes.CheckboxChecked);
              }}
            />
            <span className='not-approved-checkbox-text'>{translate('Action.Agree')}</span>
          </div>
        ) : null}
        {proceedAction !== null ? (
          <div className='not-approved-action-section-children'>
            <NotApprovedPageProceedCTAButton
              proceedAction={proceedAction}
              isDisabled={proceedAction === ProceedAction.Reactivate && !reactivateAckIsChecked}
            />
          </div>
        ) : null}
        <div className='not-approved-action-section-children' data-testid='logout-button'>
          <Button
            className='enable-three-dee btn-control'
            size={Button.sizes.medium}
            variant={Button.variants.control}
            onClick={() => {
              // eslint-disable-next-line no-void
              void logout();
              sendNotApprovedPageEvent(EventTypes.LogoutClicked);
            }}
            width={Button.widths.default}>
            {
              /* Label.sLogout is a typo that was submitted when this translation was created. :( */
              translate('Label.sLogout')
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotApprovedMainPage;
